import { useCallback } from 'react';
import { useClerk } from '@clerk/clerk-react';
import Logger from '@common/logger';

const logger = Logger('clerkErrorHandling');

/**
 * Clerk-specific error types that we need to handle
 */
export type ClerkErrorType = 
  | 'session_expired'
  | 'token_refresh_failed'
  | 'authentication_required'
  | 'network_error'
  | 'unknown_error';

/**
 * Enhanced error information for Clerk authentication errors
 */
export interface ClerkAuthError {
  type: ClerkErrorType;
  message: string;
  originalError?: any;
  shouldRetry: boolean;
  requiresReauth: boolean;
}

/**
 * Parse Clerk-specific errors and determine appropriate handling
 * @param error - The error object from API or Clerk
 * @returns ClerkAuthError with handling instructions
 */
export const parseClerkError = (error: any): ClerkAuthError => {
  // logger.log('Parsing Clerk error:', error);

  // Handle Clerk-specific error codes
  if (error?.clerkError) {
    switch (error.code) {
      case 'session_expired':
      case 'session_invalid':
        return {
          type: 'session_expired',
          message: 'Your session has expired. Please sign in again.',
          originalError: error,
          shouldRetry: false,
          requiresReauth: true
        };
      
      case 'token_invalid':
      case 'token_expired':
        return {
          type: 'token_refresh_failed',
          message: 'Authentication token is invalid. Please sign in again.',
          originalError: error,
          shouldRetry: false,
          requiresReauth: true
        };
    }
  }

  // Handle HTTP authentication errors
  if (error?.response?.status === 401) {
    return {
      type: 'authentication_required',
      // message: 'Authentication required. Please sign in.',
      message: 'Unauthorized request',
      originalError: error,
      shouldRetry: false,
      requiresReauth: false
    };
  }

  // Handle network errors
  if (error?.code === 'NETWORK_ERROR' || error?.code === 'ERR_NETWORK' || error?.message?.includes('Network')) {
    return {
      type: 'network_error',
      message: 'Network error. Please check your connection and try again.',
      originalError: error,
      shouldRetry: true,
      requiresReauth: false
    };
  }

  // Default to unknown error
  return {
    type: 'unknown_error',
    message: error?.message || 'An unexpected error occurred.',
    originalError: error,
    shouldRetry: false,
    requiresReauth: false
  };
};

/**
 * Hook for handling Clerk authentication errors
 * Provides error handling and recovery mechanisms
 */
export const useClerkErrorHandler = () => {
  const { signOut } = useClerk();

  /**
   * Perform a clean sign out with session cleanup
   * Replaces the AWS Amplify clearAmplifyCookies functionality
   */
  const performCleanSignOut = useCallback(async (): Promise<void> => {
    try {
      logger.log('Performing clean Clerk sign out...');
      
      // Sign out from Clerk (this handles all Clerk session cleanup)
      await signOut();
      
      // Clear any application-specific storage
      // Note: Clerk handles its own session storage cleanup
      sessionStorage.clear();
      
      // Clear any custom app data from localStorage (preserve user preferences)
      const keysToRemove = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && (key.startsWith('auth_') || key.startsWith('session_') || key.startsWith('token_'))) {
          keysToRemove.push(key);
        }
      }
      keysToRemove.forEach(key => localStorage.removeItem(key));
      
      logger.log('Clean sign out completed');
    } catch (error) {
      logger.error('Error during clean sign out:', error);
      // Even if cleanup fails, the signOut should have worked
    }
  }, [signOut]);

  /**
   * Handle authentication errors with appropriate recovery actions
   * @param error - The error to handle
   * @returns Promise<boolean> - Whether the error was handled successfully
   */
  const handleAuthError = useCallback(async (error: any): Promise<boolean> => {
    const parsedError = parseClerkError(error);
    
    // logger.error('Handling Clerk auth error:', parsedError);

    // Standard errors we want to bubble up to the user
    if(error.status === 413 || error.status === 500 || error.status === 400 || error.status === 404) {
      return false;
    }

    switch (parsedError.type) {
      case 'session_expired':
      case 'token_refresh_failed':
      case 'authentication_required':
        // Force sign out and redirect to login
        await performCleanSignOut();
        return true;

      case 'network_error':
        // For network errors, don't sign out, just log
        logger.log('Network error detected, not signing out user');
        return false;

      case 'unknown_error':
        // For unknown errors, try to refresh token first
        // return true;
        // try {

        //   await getToken({ skipCache: true });
        //   logger.log('Token refresh successful');
        //   return true;
        // } catch (refreshError) {
        //   logger.error('Token refresh failed:', refreshError);
        //   await performCleanSignOut();
        //   return true;
        // }

      default:
        return false;
    }
  }, [performCleanSignOut]);

  /**
   * Check if an error requires immediate authentication
   * @param error - The error to check
   * @returns boolean - Whether immediate authentication is required
   */
  const requiresImmediateAuth = useCallback((error: any): boolean => {
    const parsedError = parseClerkError(error);
    return parsedError.requiresReauth;
  }, []);

  /**
   * Retry an operation with token refresh
   * @param operation - The operation to retry
   * @param maxRetries - Maximum number of retry attempts
   * @returns Promise<any> - Result of the operation
   */
  const retryWithTokenRefresh = useCallback(async (
    operation: () => Promise<any>,
    maxRetries: number = 2
  ): Promise<any> => {
    let attempts = 0;
    
    while (attempts < maxRetries) {
      try {
        return await operation();
      } catch (error) {
        attempts++;
        
        if (attempts >= maxRetries) {
          throw error;
        }
        
        const parsedError = parseClerkError(error);
        
        if (parsedError.requiresReauth) {
          throw error; // Don't retry auth errors
        }
        
        if (parsedError.shouldRetry) {
          logger.log(`Retrying operation (attempt ${attempts + 1}/${maxRetries})`);
          
          // Try to refresh token before retry
          // try {
          //   await getToken({ skipCache: true });
          // } catch (refreshError) {
          //   logger.error('Token refresh failed during retry:', refreshError);
          //   throw error;
          // }
          
          // Wait before retrying
          await new Promise(resolve => setTimeout(resolve, 1000 * attempts));
        } else {
          throw error;
        }
      }
    }
  }, []);

  return {
    handleAuthError,
    performCleanSignOut,
    requiresImmediateAuth,
    retryWithTokenRefresh,
    parseClerkError
  };
};
